import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { CreditCard, Loader2 } from 'lucide-react';
import { useStripe } from '../../contexts/StripeContext';
import { SELF_PHONE_PRICE_LABEL } from '../../lib/selfPhoneProduct';

interface StripeCheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  isFlashKit?: boolean;
  soldOut?: boolean;
}

export default function StripeCheckoutModal({
  isOpen,
  onClose,
  isFlashKit = false,
  soldOut = false,
}: StripeCheckoutModalProps) {
  const { openCheckout } = useStripe();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCheckout = async () => {
    if (soldOut) return;

    try {
      setIsLoading(true);
      setError(null);
      await openCheckout('phone');
    } catch (err) {
      console.error('Error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-lg rounded-3xl border border-white/10 bg-black/90 p-8 backdrop-blur-xl">
          <Dialog.Title className="mb-6 text-2xl font-bold text-white">
            Complete Your {isFlashKit ? 'Flash Kit' : 'Order'}
          </Dialog.Title>

          <div className="mb-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            <h3 className="mb-2 text-lg font-semibold text-white/80">Order Summary</h3>
            <div className="mb-2 flex items-center justify-between text-white">
              <span>{isFlashKit ? 'Complete Flash Kit' : "SELF Phone Founder's Edition"}</span>
              <span>{isFlashKit ? '$149' : SELF_PHONE_PRICE_LABEL}</span>
            </div>
            <div className="text-sm text-white/50">
              {isFlashKit
                ? 'Includes AI agent, Aura Shield, Role Model AI, and full system integration.'
                : 'Includes Resonatia mesh node, satellite connectivity, Role Model AI, and Aura Shield.'}
            </div>
          </div>

          {soldOut && (
            <div className="mb-4 rounded-xl border border-white/15 bg-white/5 p-4 text-sm text-white/70">
              All 50 SELF Phones have been claimed.
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-xl border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-300">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <button
              onClick={handleCheckout}
              disabled={isLoading || soldOut}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-white px-6 py-3 font-medium text-black transition-colors hover:bg-white/90 disabled:cursor-not-allowed disabled:bg-white/30 disabled:text-white/50"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="h-5 w-5" />
                  {soldOut ? 'Sold Out' : 'Pay with Card'}
                </>
              )}
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-white/45">
              Secure payment powered by Stripe. Your data is encrypted.
            </p>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}